import type { MetricsResponse } from '../../../shared/api/types';

export type MetricStatus = 'healthy' | 'warning' | 'critical';

export class MetricsDomain {
  /**
   * Determines the health status based on CPU usage
   */
  public static getCpuHealthStatus(cpuUsage: number): MetricStatus {
    if (cpuUsage > 90) return 'critical';
    if (cpuUsage > 80) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on memory usage percentage
   */
  public static getMemoryHealthStatus(
    memoryUsagePercentage: number
  ): MetricStatus {
    if (memoryUsagePercentage > 90) return 'critical';
    if (memoryUsagePercentage > 80) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on mean response time
   */
  public static getMeanResponseTimeHealthStatus(
    meanResponseTime: number
  ): MetricStatus {
    if (meanResponseTime > 2000) return 'critical';
    if (meanResponseTime > 1000) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on 95th percentile response time
   */
  public static getP95ResponseTimeHealthStatus(
    p95ResponseTime: number
  ): MetricStatus {
    if (p95ResponseTime > 5000) return 'critical';
    if (p95ResponseTime > 2000) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on 99th percentile response time
   */
  public static getP99ResponseTimeHealthStatus(
    p99ResponseTime: number
  ): MetricStatus {
    if (p99ResponseTime > 10000) return 'critical';
    if (p99ResponseTime > 5000) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on error rate
   */
  public static getErrorRateHealthStatus(errorRate: number): MetricStatus {
    if (errorRate > 10) return 'critical';
    if (errorRate > 5) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on success rate
   */
  public static getSuccessRateHealthStatus(successRate: number): MetricStatus {
    if (successRate < 90) return 'critical';
    if (successRate < 95) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on failed requests count
   */
  public static getFailedRequestsHealthStatus(
    failedRequests: number
  ): MetricStatus {
    if (failedRequests > 100) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on current RPS
   */
  public static getCurrentRpsHealthStatus(currentRps: number): MetricStatus {
    if (currentRps > 100) return 'warning';

    return 'healthy';
  }

  /**
   * Determines the health status based on peak RPS
   */
  public static getPeakRpsHealthStatus(peakRps: number): MetricStatus {
    if (peakRps > 200) return 'warning';

    return 'healthy';
  }

  /**
   * Gets all health statuses for the complete metrics dashboard
   */
  public static getHealthStatus({
    systemMetrics,
    responseTimeMetrics,
    errorRateMetrics,
    requestsPerSecond,
  }: MetricsResponse): {
    system: {
      cpu: MetricStatus;
      memory: MetricStatus;
    };
    responseTime: {
      mean: MetricStatus;
      p95: MetricStatus;
      p99: MetricStatus;
      totalRequests: MetricStatus;
    };
    errorRate: {
      errorRate: MetricStatus;
      successRate: MetricStatus;
      failedRequests: MetricStatus;
      successfulRequests: MetricStatus;
    };
    requestsPerSecond: {
      currentRps: MetricStatus;
      averageRps: MetricStatus;
      peakRps: MetricStatus;
      totalRequestsLastMinute: MetricStatus;
    };
  } {
    return {
      system: {
        cpu: this.getCpuHealthStatus(systemMetrics.cpuUsage),
        memory: this.getMemoryHealthStatus(systemMetrics.memoryUsagePercentage),
      },
      responseTime: {
        mean: this.getMeanResponseTimeHealthStatus(responseTimeMetrics.mean),
        p95: this.getP95ResponseTimeHealthStatus(responseTimeMetrics.p95),
        p99: this.getP99ResponseTimeHealthStatus(responseTimeMetrics.p99),
        totalRequests: 'healthy', // Total requests is always healthy
      },
      errorRate: {
        errorRate: this.getErrorRateHealthStatus(errorRateMetrics.errorRate),
        successRate: this.getSuccessRateHealthStatus(
          errorRateMetrics.successRate
        ),
        failedRequests: this.getFailedRequestsHealthStatus(
          errorRateMetrics.failedRequests
        ),
        successfulRequests: 'healthy', // Successful requests is always healthy
      },
      requestsPerSecond: {
        currentRps: this.getCurrentRpsHealthStatus(
          requestsPerSecond.currentRps
        ),
        averageRps: 'healthy', // Average RPS is always healthy
        peakRps: this.getPeakRpsHealthStatus(requestsPerSecond.peakRps),
        totalRequestsLastMinute: 'healthy', // Total requests is always healthy
      },
    };
  }
}
